import { useEffect, useState } from 'react';

/**
 * Compteur animé 0 → `target`, courbe cubic-out, comme la maquette.
 *
 * L'état initial est `target`, pas 0 : c'est la valeur que le SSR écrit dans le
 * HTML et que l'hydratation retrouve à l'identique. Partir de 0 rendrait un
 * « 0 » dans la page servie — mauvais pour un visiteur sans JS, et une
 * divergence d'hydratation pour les autres. L'animation ne démarre qu'après,
 * dans l'effet, et est simplement sautée si le visiteur a demandé moins
 * d'animations.
 */
export function useCountUp(target: number, duration = 1600): number {
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}
